// 应用外壳：入席 → 大厅 → 可直接操作实体牌、骰子、棋子与座席的牌桌。

import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";

import { areaLabel, buildDragSources, promptLegalAction, waitingText } from "./actions.tsx";
import type { ObjectAction } from "./actions.tsx";
import type { DropPayload } from "./drag.tsx";
import { DragGhost, cancelDrag, dropTap, startDrag } from "./drag.tsx";
import { CardFace } from "./assets.tsx";
import { Lobby } from "./lobby.tsx";
import { cardName, roleCopy } from "./glossary.ts";
import { TooltipLayer } from "./tooltip.tsx";
import { table } from "./store.ts";
import { CardMotionLayer } from "./card-motion.tsx";
import { useTablePresentation } from "./presentation.ts";
import { ActionGuide } from "./action-guide.tsx";
import { HandFan, PlayerHUD } from "./player-hud.tsx";
import { opponentSeats } from "./table-seating.ts";
import { PlayerInspector } from "./player-inspector.tsx";
import { GameDisplayControls } from "./game-display.tsx";
import "./table-layout.css";
import "./number-font.css";
import {
  Board,
  DeckRow,
  LogPanel,
  TableEffects,
  PlayerPanel,
  TableStatus,
} from "./table.tsx";
import { campLabel } from "./types.ts";
import type { LegalAction, PlayerState, Room, State } from "./types.ts";

const STATUS_LABEL: Record<string, string> = {
  idle: "未接続",
  connecting: "接続中",
  online: "在線",
  offline: "切断",
};


const RESULT_REASON: Record<string, string> = {
  boss_all_dead: "BOSS 方全灭，自机阵营取得胜利。",
  jiki_all_dead: "自机方全灭，BOSS 阵营取得胜利。",
  yajiuma_deaths: "第三名野次马退场，BOSS 阵营取得胜利。",
  yajiuma_win: "野次马达成个人胜利条件；其他满足条件的玩家一同获胜。",
};


function SettlementOverlay({
  room,
  state,
  onClose,
  onLeave,
}: {
  room: Room | null;
  state: State;
  onClose: () => void;
  onLeave: () => void;
}) {
  const gameResult = state.result;
  const roomResult = room?.result;
  if (!gameResult && !roomResult) return null;
  const concederID = roomResult?.conceder;
  const conceder = room?.players.find((player) => player.player_id === concederID)?.name
    ?? state.players.find((player) => player.id === concederID)?.name;
  const reason = gameResult
    ? RESULT_REASON[gameResult.reason] ?? gameResult.reason
    : `${conceder ?? "一名玩家"}${roomResult?.cause === "timeout" ? "超时认输" : "离线认输"}，对局结束。`;
  const winners = new Map((gameResult?.winners ?? []).map((winner) => [winner.player, winner]));
  return (
    <section className="settlement-layer" role="dialog" aria-modal="true" aria-labelledby="settlement-title">
      <div className="settlement-panel">
        <header className="settlement-head">
          <span>GAME SETTLEMENT</span>
          <h2 id="settlement-title">勝負已定</h2>
          <p>{reason}</p>
        </header>
        {roomResult && !gameResult && (
          <div className="settlement-concession">
            <span>終局</span>
            <strong>{conceder ?? "玩家"}认输</strong>
          </div>
        )}
        <ol className="settlement-winners settlement-roster" aria-label="全员角色卡与胜负">
          {state.players.map((player, index) => {
            const winner = winners.get(player.id);
            const reviewed = player.role ? roleCopy(player.role.id) : undefined;
            const camp = player.role?.camp ?? winner?.camp ?? "";
            const conceded = roomResult?.conceder === player.id;
            return (
              <li
                key={player.id}
                className={`settlement-winner${camp ? ` camp-${camp}` : ""}${winner ? " is-winner" : ""}${conceded ? " is-conceder" : ""}`}
              >
                <span className="settlement-rank">{player.seat + 1}</span>
                <div
                  className={`settlement-card${player.role ? " is-revealing" : ""}`}
                  style={{ "--reveal-delay": `${180 + index * 120}ms` } as React.CSSProperties}
                >
                  <span className="settlement-role-front">
                    {player.role && (
                      <CardFace id={player.role.id} alt={`${reviewed?.name ?? player.role.name}角色卡`} />
                    )}
                  </span>
                </div>
                <div>
                  <small>{camp ? campLabel(camp) : "身份公开中"}</small>
                  <strong>{player.name}</strong>
                  <span>{player.role ? reviewed?.name ?? player.role.name : "正在翻开角色卡…"}</span>
                  <b>{winner ? "勝者" : conceded ? "認輸" : "終局"}</b>
                </div>
              </li>
            );
          })}
        </ol>
        <footer className="settlement-actions">
          <button type="button" className="is-ghost" onClick={onClose}>查看终局牌桌</button>
          <button type="button" className="is-primary" onClick={onLeave}>返回大厅</button>
        </footer>
      </div>
    </section>
  );
}

function EienteiChoice({ target, choices, onChoose, onCancel }: {
  target: string;
  choices: LegalAction[];
  onChoose: (action: LegalAction) => void;
  onCancel: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  useLayoutEffect(() => {
    const node = dialog.current!;
    node.showModal();
    return () => node.close();
  }, []);
  return (
    <dialog
      ref={dialog}
      className="eientei-choice"
      aria-labelledby="eientei-choice-title"
      onCancel={(event) => { event.preventDefault(); onCancel(); }}
      onClick={(event) => { if (event.target === event.currentTarget) onCancel(); }}
    >
      <h2 id="eientei-choice-title">永远亭</h2>
      <p>对 <strong>{target}</strong> 施加哪种效果？</p>
      <div className="eientei-modes">
        {choices.map((action) => (
          <button type="button" key={action.mode} className={`eientei-mode is-${action.mode}`} onClick={() => onChoose(action)}>
            {areaLabel(action)}
          </button>
        ))}
      </div>
      <button type="button" className="is-ghost" onClick={onCancel}>返回选择玩家</button>
    </dialog>
  );
}

export function App() {
  const live = useSyncExternalStore(table.subscribe, table.getSnapshot);
  const { snap, busy, event, diceOutcomes, cardEvents, motionState, handCard, motionKey, finishCards } = useTablePresentation(live);
  const [name, setName] = useState("");
  const [pinned, setPinned] = useState<string | null>(null);
  const [rollRequest, setRollRequest] = useState<{
    roomId: string | null; seq: number; playerName: string; label: string; actionLabel: string;
  } | null>(null);
  const [eienteiTarget, setEienteiTarget] = useState<string | null>(null);
  const [settlementOpen, setSettlementOpen] = useState(false);

  useEffect(() => {
    if (table.getSnapshot().me) table.connect();
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") cancelDrag();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    setEienteiTarget(null);
  }, [live.seq, live.roomId]);

  const me = snap.me;
  const room = snap.room;
  const state = snap.state;
  const playing = room?.phase === "running" || room?.phase === "finished";
  const pend = snap.pending;
  const mine = state && me ? buildDragSources(state, snap.legal, pend, me.player_id) : null;
  const waiting = state && me ? waitingText(state, snap.legal, snap.waitingFor, me.player_id) : null;
  const pendingForMe = pend?.player === me?.player_id ? pend : null;
  const placeChoices =
    (pendingForMe?.kind === "choose_place" || pendingForMe?.kind === "choose_place_card")
      ? pendingForMe.options
          .map((option) => option.value)
          .filter((value): value is number => typeof value === "number")
      : undefined;
  const pendingCard = busy ? handCard : pendingForMe?.private_info?.card ?? pendingForMe?.context?.card ?? null;
  const diceAction = mine?.table.find((action) => action.visual === "dice");
  const eienteiChoices = eienteiTarget ? snap.legal.filter((action) =>
    action.type === "area" && action.place === "eientei" && action.target === eienteiTarget,
  ) : [];
  const ownDicePresented = Boolean(rollRequest && event?.type === "dice_rolled" &&
    event.seq > rollRequest.seq && event.data?.["player"] === me?.player_id);
  const rolling = rollRequest !== null && !ownDicePresented;
  const interactionLocked = busy || rolling || eienteiTarget !== null;
  const guideKind = placeChoices?.length ? "places" : mine?.decks.size ? "decks" : mine?.players.size ? "players" : null;
  const roll = ownDicePresented ? null : rollRequest ?? (diceAction && !busy ? {
    playerName: state?.players.find((player) => player.id === me?.player_id)?.name ?? me?.name ?? "",
    label: pendingForMe?.kind === "roll_dice" && pendingCard ? `${cardName(pendingCard.id)} · 点击掷骰` : "点击掷骰",
    actionLabel: diceAction.label,
  } : null);

  useEffect(() => {
    if (rollRequest && (ownDicePresented || live.error || live.status !== "online" || live.roomId !== rollRequest.roomId)) {
      setRollRequest(null);
    }
  }, [rollRequest, ownDicePresented, live.error, live.status, live.roomId]);
  useEffect(() => {
    if (eienteiTarget && !eienteiChoices.length) setEienteiTarget(null);
  }, [eienteiTarget, eienteiChoices.length]);
  const selfIndex = state && me
    ? state.players.findIndex((player) => player.id === me.player_id)
    : 0;
  const resultKey = state?.result
    ? `${state.result.reason}:${state.result.winners.map((winner) => winner.player).join(",")}`
    : room?.result
      ? `${room.result.reason}:${room.result.conceder}:${room.result.at}`
      : "";

  useEffect(() => {
    if (resultKey) setSettlementOpen(true);
  }, [resultKey]);



  const dragStart = (event: React.PointerEvent, payload: DropPayload): void =>
    startDrag(event, payload);
  const sendObject = (object: ObjectAction): void => {
    if (interactionLocked) return;
    if (object.action.type === "area" && object.action.place === "eientei" && object.action.target) {
      setEienteiTarget(object.action.target);
      return;
    }
    if (object.visual === "dice" && roll) {
      table.clearError();
      setRollRequest({ ...roll, roomId: live.roomId, seq: live.seq });
    }
    table.send({ ...object.action });
  };
  const respond = (value: unknown): void => {
    if (!pendingForMe || interactionLocked) return;
    table.send(promptLegalAction(snap.legal, pendingForMe, value));
  };
  const playerPanel = (player: PlayerState, index: number, position = "hud", fraction = 0.5) => {
    if (!state || !me || !mine) return null;
    const isMe = player.id === me.player_id;
    return (
      <PlayerPanel
        key={player.id}
        player={player}
        themeIndex={index}
        position={position}
        seatFraction={fraction}
        isTurn={state.turn.player === player.id}
        privateRole={isMe ? snap.priv?.role_card ?? null : null}
        isMe={isMe}
        onDropTap={dropTap}
        onDragStart={dragStart}
        sealPayload={mine.ownSeal}
        actions={mine.players.get(player.id) ?? []}
        onAction={sendObject}
        pinned={pinned === player.id}
        revealAction={isMe ? mine.role : null}
        onPick={() => setPinned((current) => current === player.id ? null : player.id)}
      />
    );
  };


  return (
    <div className={`app${playing ? " is-playing" : ""}`}>
      <header className="masthead">
        <h1 className="brand">
          <ruby>
            幻想郷闘乱記
            <rt>げんそうきょう・とうらんき</rt>
          </ruby>
        </h1>
        {room && (
          <div className="masthead-room">
            <span className="room-title">{room.name}</span>
            <code>{room.room_id}</code>
            <span className={`conn conn-${snap.status}`}>
              {STATUS_LABEL[snap.status] ?? "狀態不明"}
            </span>
            <button type="button" className="is-ghost" onClick={() => void table.leaveRoom()}>
              離席
            </button>
          </div>
        )}
        {me && !room && <span className="masthead-me">在席：{me.name}</span>}
        <GameDisplayControls playing={playing} onLeave={() => void table.leaveRoom()} />
        {me && playing && state && mine && (
          <details className="log-drawer">
            <summary>巻物 <span>{snap.log.length}</span></summary>
            <LogPanel log={snap.log} />
          </details>
        )}
      </header>

      {snap.error && (
        <div className="toast" role="alert" onClick={() => table.clearError()}>
          {snap.error}
        </div>
      )}

      {state?.result || room?.result ? (
        settlementOpen ? (
          <SettlementOverlay
            room={room}
            state={state ?? {
              phase: "finished",
              mode: "",
              round: 0,
              turn: { player: "", step: "", extra_turns: 0 },
              board: { places: [], ranges: [] },
              players: [],
              decks: {},
              result: null,
            }}
            onClose={() => setSettlementOpen(false)}
            onLeave={() => void table.leaveRoom()}
          />
        ) : (
          <button type="button" className="settlement-reopen" onClick={() => setSettlementOpen(true)}>
            查看结算
          </button>
        )
      ) : null}

      {!me && (
        <main className="gate">
          <form
            className="gate-form"
            onSubmit={(event) => {
              event.preventDefault();
              void table.createSession(name.trim() || "名無し").catch((error: unknown) => {
                table.fail("入席失败", error);
              });
            }}
          >
            <h2>入席</h2>
            <p className="hint">输入名号开始；身份保存在本标签页，可多开标签页分别入席。</p>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="名号"
              maxLength={20}
            />
            <button type="submit" className="is-primary">
              入席
            </button>
          </form>
        </main>
      )}

      {me && !playing && snap.roomId && !room && (
        <main className="gate">
          <p className="hint">復元中…（正在取回对局）</p>
        </main>
      )}

      {me && !playing && (!snap.roomId || room) && <Lobby me={me} room={room} />}

      {me && playing && state && mine && (
        <main className="table-layout" aria-busy={busy || rolling} data-turn-player={state.turn.player}>
          <section className="table-stage">
            <TableStatus
              state={state}
              room={room}
              notice={pendingForMe?.text ?? waiting}
            />
            <section className="table-arena" inert={interactionLocked}>
              <div className="player-dock" aria-label="玩家座席">
                {opponentSeats(state.players, me.player_id).map(({ player, themeIndex, position, fraction }) =>
                  playerPanel(player, themeIndex, position, fraction)
                )}
              </div>

            <Board
              state={state}
              lastEvent={event?.type === "move" ? event : null}
              pickSlots={placeChoices}
              placeActions={mine.places}
              playerActions={mine.players}
              onObjectAction={sendObject}
              onPickSlot={placeChoices ? respond : undefined}
              onDropTap={dropTap}
              onPlayerPick={(playerId) => setPinned(playerId)}
            />
            </section>

            <section className="table-console" inert={interactionLocked}>
              <DeckRow decks={state.decks} payloads={mine.decks} onDragStart={dragStart} />
              <PlayerHUD
                state={state}
                priv={snap.priv}
                me={me.player_id}
                equipmentPayloads={mine.equipment}
                abilityActions={mine.abilities}
                abilityPayloads={mine.abilityDrags}
                tableActions={mine.table.filter((action) => action.visual !== "dice")}
                pending={pendingForMe}
                onRespond={respond}
                onAction={sendObject}
                onInspect={() => setPinned(me.player_id)}
                onDragStart={dragStart}
                labelOf={cardName}
              >
                {selfIndex >= 0 && playerPanel(state.players[selfIndex]!, selfIndex)}
              </PlayerHUD>
              <HandFan
                me={me.player_id}
                handCard={pendingCard}
                handPayload={mine.prompt}
                pending={pendingForMe}
                onRespond={respond}
                onDragStart={dragStart}
                labelOf={cardName}
              />
            </section>
          </section>


          {pinned && <button type="button" className="drawer-scrim" onClick={() => setPinned(null)} aria-label="关闭角色卡" />}
          <aside className={`player-drawer${pinned ? " is-open" : ""}`}>
            {pinned && (
              <PlayerInspector
                state={state}
                priv={snap.priv}
                me={me.player_id}
                targetId={pinned}
                labelOf={cardName}
                onClose={() => setPinned(null)}
              />
            )}
          </aside>


          <ActionGuide
            kind={guideKind}
            targetsKey={guideKind === "places" ? (placeChoices ?? []).join(",") : [...(guideKind === "decks" ? mine.decks : mine.players).keys()].join(",")}
            enabled={(state.turn.step === "area" || state.turn.step === "attack") && !interactionLocked && !pinned && !(settlementOpen && resultKey)}
          />
          <TableEffects
            event={rolling ? null : event}
            state={state}
            diceOutcomes={diceOutcomes}
            roll={roll ? { ...roll, rolling, onRoll: () => { if (diceAction) sendObject(diceAction); } } : null}
          />
          <CardMotionLayer key={room?.game_id ?? snap.roomId} events={cardEvents} state={motionState ?? state} me={me.player_id} sceneKey={motionKey} onComplete={finishCards} />
          {eienteiTarget && eienteiChoices.length > 0 && (
            <EienteiChoice
              target={state.players.find((player) => player.id === eienteiTarget)?.name ?? eienteiTarget}
              choices={eienteiChoices}
              onChoose={(action) => {
                if (busy || rolling) return;
                setEienteiTarget(null);
                table.send({ ...action });
              }}
              onCancel={() => setEienteiTarget(null)}
            />
          )}
        </main>
      )}

      <DragGhost />
      <TooltipLayer />
    </div>
  );
}
